import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Autocomplete,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import themestyle from "../theme";
import PhoneNumberField from "../components/PhoneNumberField";
import { getPhoneValidationMessage } from "../utils/phoneValidation";
import { textFieldSX, FormLabel, autocompleteListboxSX } from "../utils/formField";
import { CLASS_TYPE_OPTIONS, MSG, MESSAGES, REGEX, FORM_PLACEHOLDERS } from "../utils/const";
import CustomSnackbar from "../components/Snackbar";
import { resizeFile } from "../utils/imageResizer";
import { useMutation, useQuery } from "@apollo/client/react";
import { CREATE_INSTRUCTOR, UPDATE_INSTRUCTOR } from "../graphql/mutation";
import { GET_INSTRUCTOR_BY_ID, GET_INSTRUCTOR_ENGAGEMENT_REPORT, GET_INSTRUCTOR_LIST } from "../graphql/query";
import type {
  CreateInstructorInput,
  CreateInstructorResponse,
  UpdateInstructorInput,
  UpdateInstructorResponse,
  GetInstructorByIdResponse,
  InstructorFormMode,
} from "../interfaces/instructor.interface";

const INSTRUCTOR_TYPE_OPTIONS = [
  { label: "Internal", value: "INTERNAL" },
  { label: "External", value: "EXTERNAL" },
];

const CLASS_STYLE_OPTIONS = [
  { label: "Hip Hop", value: "Hip Hop" },
  { label: "Ballet", value: "Ballet" },
  { label: "Contemporary", value: "Contemporary" },
  { label: "Salsa", value: "Salsa" },
  { label: "Jazz", value: "Jazz" },
  { label: "Choreography", value: "Choreography" },
];


const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

type AddInstructorFormProps = {
  mode?: InstructorFormMode;
  id?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
  onEdit?: () => void;
  onDelete?: (id: string, name: string) => void;
};

const AddInstructorForm: React.FC<AddInstructorFormProps> = ({
  mode = "add",
  id,
  onSuccess,
  onCancel,
  onEdit,
  onDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const profileInputRef = useRef<HTMLInputElement>(null);
  const idInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    fullname: "",
    instructorType: "INTERNAL",
    instructorId: "",
    email: "",
    password: "",
    contactNumber: "",
    instagramId: "",
    address: "",
    classStyle: "",
    classType: "",
    studentsCount: "",
    about: "",
  });


  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [profileCleared, setProfileCleared] = useState(false);

  const [idFile, setIdFile] = useState<File | null>(null);
  const [idPreview, setIdPreview] = useState<string | null>(null);
  const [idCleared, setIdCleared] = useState(false);

  const [galleryFiles, setGalleryFiles] = useState<(File | null)[]>([null, null, null]);
  const [galleryPreviews, setGalleryPreviews] = useState<(string | null)[]>([null, null, null]);
  const [activeGalleryIndex, setActiveGalleryIndex] = useState<number | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState<"success" | "error">("success");
  const [showPassword, setShowPassword] = useState(false);

  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  const { data: instructorData } = useQuery<GetInstructorByIdResponse, { id: string }>(
    GET_INSTRUCTOR_BY_ID,
    {
      variables: { id: id || "" },
      skip: mode === "add" || !id,
      fetchPolicy: "network-only",
    },
  );

  const [createInstructor, { loading: createLoading }] = useMutation<
    CreateInstructorResponse,
    { input: CreateInstructorInput }
  >(CREATE_INSTRUCTOR, {
    refetchQueries: [{ query: GET_INSTRUCTOR_LIST, variables: { page: 1, perPage: 100 } }, { query: GET_INSTRUCTOR_ENGAGEMENT_REPORT }],
  });

  const [updateInstructor, { loading: updateLoading }] = useMutation<
    UpdateInstructorResponse,
    { id: string; input: UpdateInstructorInput }
  >(UPDATE_INSTRUCTOR, {
    refetchQueries: [{ query: GET_INSTRUCTOR_LIST, variables: { page: 1, perPage: 100 } }, { query: GET_INSTRUCTOR_ENGAGEMENT_REPORT }],
  });

  const loading = createLoading || updateLoading;

  useEffect(() => {
    if (instructorData?.getInstructor?.success && instructorData.getInstructor.data) {
      const data = instructorData.getInstructor.data;
      setFormData({
        fullname: data.fullname || "",
        instructorType: data.instructorType || "INTERNAL",
        instructorId: data.instructorId || "",
        email: data.email || "",
        password: "",
        contactNumber: data.contactNumber || "",
        instagramId: data.instagramId || "",
        address: data.address || "",
        classStyle: data.classStyle || "",
        classType: data.classType || "",
        studentsCount: data.studentsCount ? String(data.studentsCount) : "",
        about: data.about || "",
      });
      if (data.profileImage) {
        setProfilePreview(`data:image/*;base64,${data.profileImage}`);
      }
      if (data.idProof) {
        const isPdf = data.idProof.startsWith("JVBERi0");
        const prefix = isPdf ? "data:application/pdf;base64," : "data:image/*;base64,";
        setIdPreview(`${prefix}${data.idProof}`);
      }
      if (data.gallery && Array.isArray(data.gallery)) {
        const previews: (string | null)[] = [null, null, null];
        data.gallery.forEach((img: string, i: number) => {
          if (i < 3) previews[i] = `data:image/*;base64,${img}`;
        });
        setGalleryPreviews(previews);
        const files: (string | null)[] = [null, null, null];
        data.gallery.forEach((img: string, i: number) => {
          if (i < 3) files[i] = img;
        });
        setGalleryFiles(files as any);
      }
      setProfileCleared(false);
      setIdCleared(false);
    }
  }, [instructorData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateField = (name: string, value: string) => {
    const trimmed = typeof value === "string" ? value.trim() : value;

    if (name === "fullname") {
      if (!trimmed) return "Full name is required";
      if (trimmed.length < 2) return "Name must be at least 2 characters";
      if (!/^[A-Za-z\s]+$/.test(trimmed)) return "Name can only contain letters and spaces";
      return "";
    }
    if (name === "email") {
      if (!trimmed) return "Email is required";
      if (!EMAIL_REGEX.test(trimmed)) return "Invalid email address";
      return "";
    }
    if (name === "password") {
      if (mode !== "add") return "";
      if (!trimmed) return MESSAGES.PASS_REQUIRED;
      if (trimmed.length < 8) return MESSAGES.PASSWORD_MIN;
      if (!REGEX.UPPERCASE.test(trimmed)) return MESSAGES.PASSWORD_UPPER;
      if (!REGEX.LOWERCASE.test(trimmed)) return MESSAGES.PASSWORD_LOWER;
      if (!REGEX.NUMBER.test(trimmed)) return MESSAGES.PASSWORD_NUMBER;
      if (!REGEX.SPECIAL_CHAR.test(trimmed)) return MESSAGES.PASSWORD_SPECIAL;
      return "";
    }
    if (name === "contactNumber") {
      if (!trimmed) return MSG.PHONE_REQUIRED;
      // PhoneNumberField stores "+<dialCode> <national>"; libphonenumber validates it accurately.
      return getPhoneValidationMessage(trimmed);
    }
    if (name === "address") {
      if (!trimmed) return "Address is required";
      if (trimmed.length < 5) return "Address must be at least 5 characters";
      return "";
    }
    if (name === "instructorType" && !trimmed) return "Instructor type is required";
    if (name === "classStyle" && !trimmed) return "Class style is required";
    if (name === "classType" && !trimmed) return "Class type is required";
    if (name === "studentsCount") {
      if (!trimmed) return "";
      const count = Number(trimmed);
      if (Number.isNaN(count) || count < 0) return "Students count must be 0 or greater";
    }
    return "";
  };

  const isFormValid = () => {
    const requiredFields = [
      "fullname",
      "instructorType",
      "email",
      ...(mode === "add" ? ["password"] : []),
      "contactNumber",
      "address",
      "classStyle",
      "classType",
    ];
    for (const field of requiredFields) {
      const value = (formData as any)[field] || "";
      if (validateField(field, value)) return false;
    }
    return true;
  };

  const validateFile = (file: File) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      return MSG.INVALID_FILE_FORMAT;
    }
    if (file.size > 10 * 1024 * 1024) {
      return MSG.FILE_SIZE_ERROR;
    }
    return "";
  };

  const handleProfileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setErrors((prev) => ({ ...prev, profileImage: error }));
      return;
    }

    try {
      const resizedFile = await resizeFile(file);
      setProfileFile(resizedFile);
      setProfilePreview(URL.createObjectURL(resizedFile));
      setProfileCleared(false);
      setErrors((prev) => ({ ...prev, profileImage: "" }));
    } catch (err) {
      console.error(err);
      setProfileFile(file);
      setProfilePreview(URL.createObjectURL(file));
      setProfileCleared(false);
      setErrors((prev) => ({ ...prev, profileImage: "" }));
    }
  };

  const handleIdChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setErrors((prev) => ({ ...prev, idProof: error }));
      return;
    }

    if (file.type.startsWith("image/")) {
      try {
        const resizedFile = await resizeFile(file);
        setIdFile(resizedFile);
        setIdPreview(URL.createObjectURL(resizedFile));
        setIdCleared(false);
        setErrors((prev) => ({ ...prev, idProof: "" }));
      } catch (err) {
        console.error(err);
        setIdFile(file);
        setIdPreview(URL.createObjectURL(file));
        setIdCleared(false);
        setErrors((prev) => ({ ...prev, idProof: "" }));
      }
    } else {
      setIdFile(file);
      setIdPreview(URL.createObjectURL(file));
      setIdCleared(false);
      setErrors((prev) => ({ ...prev, idProof: "" }));
    }
  };

  const handleGalleryChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (activeGalleryIndex === null) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const error = validateFile(file);
    if (error) {
      setErrors((prev) => ({ ...prev, gallery: error }));
      return;
    }

    try {
      const resizedFile = await resizeFile(file);
      const nextFiles = [...galleryFiles];
      nextFiles[activeGalleryIndex] = resizedFile;
      setGalleryFiles(nextFiles);
      const nextPreviews = [...galleryPreviews];
      nextPreviews[activeGalleryIndex] = URL.createObjectURL(resizedFile);
      setGalleryPreviews(nextPreviews);
      setErrors((prev) => ({ ...prev, gallery: "" }));
    } catch (err) {
      console.error(err);
      const nextFiles = [...galleryFiles];
      nextFiles[activeGalleryIndex] = file;
      setGalleryFiles(nextFiles);
      const nextPreviews = [...galleryPreviews];
      nextPreviews[activeGalleryIndex] = URL.createObjectURL(file);
      setGalleryPreviews(nextPreviews);
      setErrors((prev) => ({ ...prev, gallery: "" }));
    } finally {
      setActiveGalleryIndex(null);
      if (galleryInputRef.current) galleryInputRef.current.value = "";
    }
  };

  const handleRemoveGallery = (index: number) => {
    const nextFiles = [...galleryFiles];
    nextFiles[index] = null;
    setGalleryFiles(nextFiles);
    const nextPreviews = [...galleryPreviews];
    nextPreviews[index] = null;
    setGalleryPreviews(nextPreviews);
  };


  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(",")[1]);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    const fieldErrors: Record<string, string> = {};
    Object.entries(formData).forEach(([key, value]) => {
      if (key === "password" && mode !== "add") return;
      const message = validateField(key, value);
      if (message) fieldErrors[key] = message;
    });

    if (!formData.instructorType) {
      fieldErrors.instructorType = "Instructor type is required";
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrors(fieldErrors);
      return;
    }

    const [profileBase64, idBase64, galleryBase64] = await Promise.all([
      profileFile ? fileToBase64(profileFile) : Promise.resolve(profileCleared ? null : undefined),
      idFile ? fileToBase64(idFile) : Promise.resolve(idCleared ? null : undefined),
      Promise.all(
        galleryFiles.map(async (f) => {
          if (!f) return null;
          if (typeof f === "string") return f;
          return await fileToBase64(f);
        })
      ),
    ]);

    const input: any = {
      fullname: formData.fullname,
      ...(mode === "add" ? { email: formData.email, password: formData.password } : {}),
      contactNumber: formData.contactNumber,
      instructorType: formData.instructorType,
      instagramId: formData.instagramId || undefined,
      address: formData.address,
      classStyle: formData.classStyle || undefined,
      classType: formData.classType || undefined,
      profileImage: profileBase64,
      idProof: idBase64,
      gallery: (galleryBase64 as (string | null)[]).filter((f) => f !== null) as string[],
      about: formData.about || undefined,
    };

    try {
      if (mode === "edit" && id) {
        const { data } = await updateInstructor({ variables: { id, input } });
        if (!data?.updateInstructor?.success) {
          setSnackbarMessage(data?.updateInstructor?.message || "Failed to update instructor");
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(data.updateInstructor.message || "Instructor updated");
        setSnackbarType("success");
      } else {
        const { data } = await createInstructor({ variables: { input } });
        if (!data?.createInstructor?.success) {
          setSnackbarMessage(data?.createInstructor?.message || "Failed to create instructor");
          setSnackbarType("error");
          setSnackbarOpen(true);
          return;
        }
        setSnackbarMessage(data.createInstructor.message || "Instructor created");
        setSnackbarType("success");
      }
      setSnackbarOpen(true);
      if (onSuccess) {
        setTimeout(onSuccess, 900);
      }
    } catch (err: any) {
      console.error(err);
      const errMsg =
        err?.graphQLErrors?.[0]?.message ||
        err?.networkError?.message ||
        err?.message ||
        "Something went wrong";
      setSnackbarMessage(errMsg);
      setSnackbarType("error");
      setSnackbarOpen(true);
    }
  };

  const uploadBoxSx = {
    border: `1px dashed ${themestyle.colors.uploadBorder}`,
    borderRadius: "12px",
    p: isMobile ? 1.5 : 2.5,
    textAlign: "center" as const,
    cursor: isViewMode ? "default" : "pointer",
    bgcolor: themestyle.colors.uploadBg,
    position: "relative" as const,
  };

  return (
    <Box display="flex" flexDirection="column" gap={3}>
      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "20px",
          border: "1px solid #E7EEDC",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: themestyle.colors.lightGreenBg,
            px: 3,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            fontSize="20px"
            fontWeight={700}
            color={themestyle.colors.activeGreen}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            Personal Information
          </Typography>
        </Box>

        <Box
          p={3}
          sx={{
            px: { xs: 2, sm: 3, md: 5 },
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 3,
            alignItems: "start",
          }}
        >
          <Box>
            <FormLabel>
              Full Name <span style={{ color: themestyle.colors.error }}>*</span>
            </FormLabel>
            <TextField
              fullWidth
              sx={textFieldSX}
              name="fullname"
              value={formData.fullname}
              placeholder="Enter full name"
              onChange={handleChange}
              error={!!errors.fullname}
              helperText={errors.fullname}
              disabled={isViewMode}
              onBlur={(e) =>
                setErrors((prev) => ({
                  ...prev,
                  fullname: validateField("fullname", e.target.value),
                }))
              }
            />
          </Box>
          <Box>
            <FormLabel>
              Instructor Type <span style={{ color: themestyle.colors.error }}>*</span>
            </FormLabel>
            <RadioGroup
              row
              value={formData.instructorType}
              onChange={(e) => {
                if (isViewMode) return;
                setFormData((prev) => ({ ...prev, instructorType: e.target.value }));
              }}
            >
              {INSTRUCTOR_TYPE_OPTIONS.map((option) => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio sx={{ color: themestyle.colors.activeGreen }} />}
                  label={option.label}
                />
              ))}
            </RadioGroup>
            {errors.instructorType && (
              <Typography color="error" variant="caption">
                {errors.instructorType}
              </Typography>
            )}
          </Box>
          <Box>
            <FormLabel>
              Email Address <span style={{ color: themestyle.colors.error }}>*</span>
            </FormLabel>
            <TextField
              fullWidth
              sx={textFieldSX}
              name="email"
              value={formData.email}
              autoComplete="off"
              placeholder="example@mail.com"
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              disabled={isViewMode || mode === "edit"}
              onBlur={(e) =>
                setErrors((prev) => ({
                  ...prev,
                  email: validateField("email", e.target.value),
                }))
              }
            />
          </Box>
          {mode === "add" && (
            <Box>
              <FormLabel>
                Password <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <TextField
                fullWidth
                sx={textFieldSX}
                name="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                autoComplete="new-password"
                placeholder="Minimum 8 characters"
                onChange={handleChange}
                error={!!errors.password}
                helperText={errors.password}
                disabled={isViewMode}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showPassword ? "Hide password" : "Show password"}
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? (
                          <VisibilityIcon fontSize="small" />
                        ) : (
                          <VisibilityOffIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                onBlur={(e) =>
                  setErrors((prev) => ({
                    ...prev,
                    password: validateField("password", e.target.value),
                  }))
                }
              />
            </Box>
          )}
          <Box>
            <FormLabel>
              Phone Number <span style={{ color: themestyle.colors.error }}>*</span>
            </FormLabel>
            <PhoneNumberField
              value={formData.contactNumber}
              placeholder={FORM_PLACEHOLDERS.PHONE_UK}
              disabled={isViewMode}
              error={!!errors.contactNumber}
              helperText={errors.contactNumber}
              onChange={(next) => {
                setFormData((prev) => ({ ...prev, contactNumber: next }));
                setErrors((prev) => ({
                  ...prev,
                  contactNumber: validateField("contactNumber", next),
                }));
              }}
              onBlur={() =>
                setErrors((prev) => ({
                  ...prev,
                  contactNumber: validateField("contactNumber", formData.contactNumber),
                }))
              }
            />
          </Box>
          <Box>
            <FormLabel>Instagram ID</FormLabel>
            <TextField
              fullWidth
              sx={textFieldSX}
              name="instagramId"
              value={formData.instagramId}
              placeholder="@username"
              onChange={handleChange}
              disabled={isViewMode}
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "auto", md: "1 / -1" } }}>
            <FormLabel>
              Address <span style={{ color: themestyle.colors.error }}>*</span>
            </FormLabel>
            <TextField
              fullWidth
              sx={textFieldSX}
              name="address"
              value={formData.address}
              placeholder="Enter your full home address"
              onChange={handleChange}
              error={!!errors.address}
              helperText={errors.address}
              disabled={isViewMode}
              multiline
              rows={3}
              onBlur={(e) =>
                setErrors((prev) => ({
                  ...prev,
                  address: validateField("address", e.target.value),
                }))
              }
            />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "20px",
          border: "1px solid #E7EEDC",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: themestyle.colors.lightGreenBg,
            px: 3,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            fontSize="20px"
            fontWeight={700}
            color={themestyle.colors.activeGreen}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            Class & Stats
          </Typography>
        </Box>

        <Box p={3} display="flex" flexDirection="column" gap={3} sx={{ px: { xs: 2, sm: 3, md: 5 } }}>
          <Box display="flex" gap={3} flexDirection={{ xs: "column", md: "row" }}>
            <Box flex={1}>
              <FormLabel>
                Class Style <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <Autocomplete
                options={CLASS_STYLE_OPTIONS}
                disabled={isViewMode}
                value={CLASS_STYLE_OPTIONS.find((opt) => opt.value === formData.classStyle) || null}
                onChange={(_, value) => {
                  if (isViewMode) return;
                  const val = value?.value || "";
                  setFormData((prev) => ({ ...prev, classStyle: val }));
                  setErrors((prev) => ({ ...prev, classStyle: validateField("classStyle", val) }));
                }}
                slotProps={{
                  listbox: { sx: autocompleteListboxSX },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Style"
                    sx={textFieldSX}
                    error={!!errors.classStyle}
                    helperText={errors.classStyle}
                  />
                )}
              />
            </Box>
            <Box flex={1}>
              <FormLabel>
                Class Type <span style={{ color: themestyle.colors.error }}>*</span>
              </FormLabel>
              <Autocomplete
                options={CLASS_TYPE_OPTIONS}
                disabled={isViewMode}
                value={CLASS_TYPE_OPTIONS.find((opt) => opt.value === formData.classType) || null}
                onChange={(_, value) => {
                  if (isViewMode) return;
                  const val = value?.value || "";
                  setFormData((prev) => ({ ...prev, classType: val }));
                  setErrors((prev) => ({ ...prev, classType: validateField("classType", val) }));
                }}
                slotProps={{
                  listbox: { sx: autocompleteListboxSX },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select Level"
                    sx={textFieldSX}
                    error={!!errors.classType}
                    helperText={errors.classType}
                  />
                )}
              />
            </Box>
          </Box>


        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: themestyle.colors.white,
          borderRadius: "20px",
          border: "1px solid #E7EEDC",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            backgroundColor: themestyle.colors.lightGreenBg,
            px: 3,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            fontSize="20px"
            fontWeight={700}
            color={themestyle.colors.activeGreen}
            fontFamily={themestyle.fontFamily.Roboto}
          >
            Media & Identification
          </Typography>
        </Box>

        <Box p={3} display="flex" flexDirection="column" gap={3} sx={{ px: { xs: 2, sm: 3, md: 5 } }}>
          <Box display="flex" gap={3} flexDirection={{ xs: "column", md: "row" }}>
            <Box flex={1}>
              <FormLabel>Profile Image</FormLabel>
              <Box
                sx={{
                  ...uploadBoxSx,
                  width: 140,
                  height: 140,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => !isViewMode && profileInputRef.current?.click()}
              >
                <input
                  ref={profileInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleProfileChange}
                  disabled={isViewMode}
                />
                {profilePreview ? (
                  <>
                    <Box
                      component="img"
                      src={profilePreview}
                      alt="Profile Preview"
                      sx={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "10px" }}
                    />
                    {!isViewMode && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setProfileFile(null);
                          setProfilePreview(null);
                          setProfileCleared(true);
                        }}
                        sx={{ position: "absolute", top: 6, right: 6, bgcolor: themestyle.colors.white }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </>
                ) : (
                  <AddIcon sx={{ color: themestyle.colors.activeGreen }} />
                )}
              </Box>
              {errors.profileImage && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                  {errors.profileImage}
                </Typography>
              )}
            </Box>
            <Box flex={2}>
              <FormLabel>Upload ID (Government Issued)</FormLabel>
              <Box
                sx={{ ...uploadBoxSx, minHeight: 120 }}
                onClick={() => !isViewMode && idInputRef.current?.click()}
              >
                <input
                  ref={idInputRef}
                  type="file"
                  hidden
                  accept="image/*,application/pdf"
                  onChange={handleIdChange}
                  disabled={isViewMode}
                />
                {idPreview ? (
                  idFile?.type === "application/pdf" ||
                  idFile?.name.toLocaleLowerCase().endsWith(".pdf") ||
                  idPreview.includes("application/pdf") ||
                  idPreview.includes("data:application/pdf") ||
                  idPreview.includes("JVBERi0") ? (
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                      <PictureAsPdfIcon sx={{ fontSize: 48, color: "#f44336" }} />
                      <Typography variant="caption" sx={{ color: "text.secondary", maxWidth: "100%", textAlign: "center" }}>
                        {idFile ? idFile.name : "Government ID (PDF)"}
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      component="img"
                      src={idPreview}
                      alt="ID Preview"
                      sx={{ maxHeight: 110, maxWidth: "100%", objectFit: "contain" }}
                    />
                  )
                ) : (
                  <>
                    <CloudUploadOutlinedIcon sx={{ color: themestyle.colors.activeGreen, fontSize: 32 }} />
                    <Typography mt={1} fontSize={"14px"}>
                      Click to <b style={{ color: themestyle.colors.activeGreen }}>Upload</b>
                    </Typography>
                    <Typography fontSize="12px" color="text.secondary">
                      PDF, JPG, PNG
                    </Typography>
                  </>
                )}
                {idPreview && !isViewMode && (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setIdFile(null);
                      setIdPreview(null);
                      setIdCleared(true);
                    }}
                    sx={{ position: "absolute", top: 6, right: 6, bgcolor: themestyle.colors.white }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              {errors.idProof && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                  {errors.idProof}
                </Typography>
              )}
            </Box>
          </Box>

          <Box>
            <FormLabel>Instructor Gallery (Multiple Images)</FormLabel>
            <Box display="flex" gap={2} flexWrap="wrap">
              {galleryPreviews.map((preview, index) => {
                return (
                  <Box
                    key={index}
                    sx={{
                      ...uploadBoxSx,
                      width: 120,
                      height: 120,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    onClick={() => {
                      if (!isViewMode) {
                        setActiveGalleryIndex(index);
                        galleryInputRef.current?.click();
                      }
                    }}
                  >
                    {preview ? (
                      <>
                        <Box
                          component="img"
                          src={preview}
                          alt={`Gallery ${index + 1}`}
                          sx={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "10px" }}
                        />
                        {!isViewMode && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveGallery(index);
                            }}
                            sx={{ position: "absolute", top: 6, right: 6, bgcolor: themestyle.colors.white }}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        )}
                      </>
                    ) : (
                      <AddIcon sx={{ color: themestyle.colors.activeGreen }} />
                    )}
                  </Box>
                );
              })}
              <input
                ref={galleryInputRef}
                type="file"
                hidden
                accept="image/*"
                onChange={handleGalleryChange}
                disabled={isViewMode}
              />
            </Box>
            {errors.gallery && (
              <Typography color="error" variant="caption" sx={{ mt: 0.5, display: "block" }}>
                {errors.gallery}
              </Typography>
            )}
          </Box>

          <Box>
            <FormLabel>About Instructor</FormLabel>
            <TextField
              name="about"
              fullWidth
              multiline
              rows={3}
              placeholder="Tell us about the instructor's background and experience..."
              value={formData.about}
              onChange={handleChange}
              sx={textFieldSX}
              disabled={isViewMode}
            />
          </Box>

          <Box
            sx={{
              px: isMobile ? 1 : { sm: 3, md: 5 },
              pb: 3,
              display: "flex",
              justifyContent: "flex-end",
              gap: isMobile ? 1 : 2,
              mt: 1,
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => onCancel && onCancel()}
              sx={{
                borderColor: themestyle.colors.buttonGreen,
                color: themestyle.colors.black,
                textTransform: "none",
                fontWeight: 600,
                px: isMobile ? 1.5 : 2,
                borderRadius: "8px",
                height: isMobile ? "36px" : "44px",
                fontSize: isMobile ? "13px" : "16px",
                minWidth: isMobile ? "75px" : "110px",
              }}
            >
              Cancel
            </Button>
            {isViewMode && onEdit ? (
              <Button
                type="button"
                variant="contained"
                onClick={onEdit}
                sx={{
                  backgroundColor: themestyle.colors.buttonGreen,
                  textTransform: "none",
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  px: isMobile ? 1.5 : 5,
                  height: isMobile ? "36px" : "44px",
                  fontSize: isMobile ? "13px" : "16px",
                  minWidth: isMobile ? "100px" : "110px",
                  borderRadius: "8px",
                  "&:hover": {
                    backgroundColor: themestyle.colors.buttonGreenHover,
                  },
                }}
              >
                Edit
              </Button>
            ) : (
              !isViewMode && (
                <>
                  <Button
                    type="button"
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !isFormValid()}
                    sx={{
                      backgroundColor: themestyle.colors.buttonGreen,
                      textTransform: "none",
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      px: isMobile ? 1.5 : 5,
                      height: isMobile ? "36px" : "44px",
                      fontSize: isMobile ? "13px" : "16px",
                      minWidth: isMobile ? "100px" : "110px",
                      borderRadius: "8px",
                      "&:hover": {
                        backgroundColor: themestyle.colors.buttonGreenHover,
                      },
                    }}
                  >
                    {mode === "add" ? "Save" : "Update"}
                  </Button>
                  {isMobile && isEditMode && id && onDelete && (
                    <Button
                      variant="contained"
                      onClick={() => onDelete(id, formData.fullname)}
                      sx={{
                        backgroundColor: themestyle.colors.error,
                        textTransform: "none",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                        px: 1.5,
                        height: "36px",
                        fontSize: "13px",
                        minWidth: "75px",
                        borderRadius: "8px",
                        "&:hover": {
                          backgroundColor: themestyle.colors.error,
                        },
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </>
              )
            )}
          </Box>
        </Box>
      </Box>

      <CustomSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        type={snackbarType}
        onClose={() => setSnackbarOpen(false)}
      />
    </Box>
  );
};

export default AddInstructorForm;
